import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import { createElement } from "@wordpress/element";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Date.prototype.toString.call(Reflect.construct(Date, [], function () {})); return true; } catch (e) { return false; } }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Parts of this source were derived and modified from react-color,
 * released under the MIT license.
 *
 * https://github.com/casesandberg/react-color/
 *
 * Copyright (c) 2015 Case Sandberg
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */

/**
 * External dependencies
 */
import classnames from 'classnames';
import { debounce, noop, partial } from 'lodash';
/**
 * WordPress dependencies
 */

import { Component } from '@wordpress/element';
/**
 * Internal dependencies
 */

import Alpha from './alpha';
import Hue from './hue';
import Inputs from './inputs';
import Saturation from './saturation';
import { colorToState, simpleCheckForValidColor, isValidHex } from './utils';

var toLowerCase = function toLowerCase(value) {
  return String(value).toLowerCase();
};

var isValueEmpty = function isValueEmpty(data) {
  if (data.source === 'hex' && !data.hex) {
    return true;
  } else if (data.source === 'hsl' && (!data.h || !data.s || !data.l)) {
    return true;
  } else if (data.source === 'rgb' && (!data.r || !data.g || !data.b) && (!data.h || !data.s || !data.v || !data.a) && (!data.h || !data.s || !data.l || !data.a)) {
    return true;
  }

  return false;
};

var isValidColor = function isValidColor(colors) {
  return colors.hex ? isValidHex(colors.hex) : simpleCheckForValidColor(colors);
};
/**
 * Function that creates the new color object
 * from old data and the new value.
 *
 * @param {Object} oldColors The old color object.
 * @param {string} oldColors.hex
 * @param {Object} oldColors.rgb
 * @param {number} oldColors.rgb.r
 * @param {number} oldColors.rgb.g
 * @param {number} oldColors.rgb.b
 * @param {number} oldColors.rgb.a
 * @param {Object} oldColors.hsl
 * @param {number} oldColors.hsl.h
 * @param {number} oldColors.hsl.s
 * @param {number} oldColors.hsl.l
 * @param {number} oldColors.hsl.a
 * @param {string} oldColors.draftHex Same format as oldColors.hex
 * @param {Object} oldColors.draftRgb Same format as oldColors.rgb
 * @param {Object} oldColors.draftHsl Same format as oldColors.hsl
 * @param {Object} data Data containing the new value to update.
 * @param {Object} data.source One of `hex`, `rgb`, `hsl`.
 * @param {string|number} data.value Value to update.
 * @param {string} data.valueKey Depends on `data.source` values:
 *   - when source = `rgb`, valuKey can be `r`, `g`, `b`, or `a`.
 *   - when source = `hsl`, valuKey can be `h`, `s`, `l`, or `a`.
 * @return {Object} A new color object for a specific source. For example:
 * { source: 'rgb', r: 1, g: 2, b:3, a:0 }
 */


var dataToColors = function dataToColors(oldColors, _ref) {
  var source = _ref.source,
      valueKey = _ref.valueKey,
      value = _ref.value;

  if (source === 'hex') {
    return _defineProperty({
      source: source
    }, source, value);
  }

  return _objectSpread({
    source: source
  }, _objectSpread(_objectSpread({}, oldColors[source]), _defineProperty({}, valueKey, value)));
};

var ColorPicker = /*#__PURE__*/function (_Component) {
  _inherits(ColorPicker, _Component);

  var _super = _createSuper(ColorPicker);

  function ColorPicker(_ref3) {
    var _this;

    var _ref3$color = _ref3.color,
        color = _ref3$color === void 0 ? '0071a1' : _ref3$color;

    _classCallCheck(this, ColorPicker);

    _this = _super.apply(this, arguments);
    var colors = colorToState(color);
    _this.state = _objectSpread(_objectSpread({}, colors), {}, {
      draftHex: toLowerCase(colors.hex),
      draftRgb: colors.rgb,
      draftHsl: colors.hsl
    });
    _this.commitValues = _this.commitValues.bind(_assertThisInitialized(_this));
    _this.setDraftValues = _this.setDraftValues.bind(_assertThisInitialized(_this));
    _this.resetDraftValues = _this.resetDraftValues.bind(_assertThisInitialized(_this));
    _this.handleInputChange = _this.handleInputChange.bind(_assertThisInitialized(_this));
    return _this;
  }

  _createClass(ColorPicker, [{
    key: "commitValues",
    value: function commitValues(data) {
      var _this$props = this.props,
          oldHue = _this$props.oldHue,
          _this$props$onChangeC = _this$props.onChangeComplete,
          onChangeComplete = _this$props$onChangeC === void 0 ? noop : _this$props$onChangeC;

      if (isValidColor(data)) {
        var colors = colorToState(data, data.h || oldHue);
        this.setState(_objectSpread(_objectSpread({}, colors), {}, {
          draftHex: toLowerCase(colors.hex),
          draftHsl: colors.hsl,
          draftRgb: colors.rgb
        }), debounce(partial(onChangeComplete, colors), 100));
      }
    }
  }, {
    key: "resetDraftValues",
    value: function resetDraftValues() {
      this.setState({
        draftHex: this.state.hex,
        draftHsl: this.state.hsl,
        draftRgb: this.state.rgb
      });
    }
  }, {
    key: "setDraftValues",
    value: function setDraftValues(data) {
      switch (data.source) {
        case 'hex':
          this.setState({
            draftHex: toLowerCase(data.hex)
          });
          break;

        case 'rgb':
          this.setState({
            draftRgb: data
          });
          break;

        case 'hsl':
          this.setState({
            draftHsl: data
          });
          break;
      }
    }
  }, {
    key: "handleInputChange",
    value: function handleInputChange(data) {
      switch (data.state) {
        case 'reset':
          this.resetDraftValues();
          break;

        case 'commit':
          var colors = dataToColors(this.state, data);

          if (!isValueEmpty(colors)) {
            this.commitValues(colors);
          }

          break;

        case 'draft':
          this.setDraftValues(dataToColors(this.state, data));
          break;
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props2 = this.props,
          className = _this$props2.className,
          disableAlpha = _this$props2.disableAlpha;
      var _this$state = this.state,
          color = _this$state.color,
          hsl = _this$state.hsl,
          hsv = _this$state.hsv,
          rgb = _this$state.rgb,
          draftHex = _this$state.draftHex,
          draftHsl = _this$state.draftHsl,
          draftRgb = _this$state.draftRgb;
      var classes = classnames(className, {
        'components-color-picker': true,
        'is-alpha-disabled': disableAlpha,
        'is-alpha-enabled': !disableAlpha
      });
      return createElement("div", {
        className: classes
      }, createElement("div", {
        className: "components-color-picker__saturation"
      }, createElement(Saturation, {
        hsl: hsl,
        hsv: hsv,
        onChange: this.commitValues
      })), createElement("div", {
        className: "components-color-picker__body"
      }, createElement("div", {
        className: "components-color-picker__controls"
      }, createElement("div", {
        className: "components-color-picker__swatch"
      }, createElement("div", {
        className: "components-color-picker__active",
        style: {
          backgroundColor: color && color.toRgbString()
        }
      })), createElement("div", {
        className: "components-color-picker__toggles"
      }, createElement(Hue, {
        hsl: hsl,
        onChange: this.commitValues
      }), disableAlpha ? null : createElement(Alpha, {
        rgb: rgb,
        hsl: hsl,
        onChange: this.commitValues
      }))), createElement(Inputs, {
        rgb: draftRgb,
        hsl: draftHsl,
        hex: draftHex,
        onChange: this.handleInputChange,
        disableAlpha: disableAlpha
      })));
    }
  }]);

  return ColorPicker;
}(Component);

export { ColorPicker as default };
//# sourceMappingURL=index.js.map